import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Input,
  InputNumber,
  Modal,
  Radio,
  Result,
  Select,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  FileExcelOutlined,
  FieldTimeOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  NumberOutlined,
  ReloadOutlined,
  PartitionOutlined,
  SearchOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import axios from "axios";
import * as XLSX from "xlsx";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiUrl } from "../config";
import TableContext from "../Context/TablePosition";
import useTableBodyHeight from "../utils/useTableBodyHeight";

/**
 * SAP Siparişleri Sayfası
 *
 * Boyahane planındaki satırları, plastik fabrikasıyla aynı SAP formatında
 * (GET /external/sap/orders) listeler. Her plan satırı gövde ve gövde ön
 * için ayrı birer sipariş kaydı üretir (item 1 = gövde, 2 = gövde ön).
 *
 * Plastik ekranındaki SapPage.jsx'in boyahane karşılığıdır:
 * - Plan seçimi (son plan / önceki planlar ya da doğrudan order_batch_id)
 * - Sadece aktif (asması tamamlanmamış) siparişleri gösterme
 * - Malzeme / tip / durum filtreleri ve Excel dışa aktarma
 */

const PLAN_OPTIONS = [1, 2, 3, 4, 5];

/** "YYYY-MM-DD" + "HH:MM:SS" → "DD.MM.YYYY HH:MM" */
const formatDateTime = (date, time) => {
  if (!date) return "-";
  const [y, m, d] = String(date).split("-");
  const hm = time ? String(time).slice(0, 5) : "";
  if (!y || !m || !d) return `${date} ${hm}`.trim();
  return `${d}.${m}.${y} ${hm}`.trim();
};

const toTimestamp = (date, time) => {
  const ts = new Date(`${date}T${time || "00:00:00"}`).getTime();
  return Number.isNaN(ts) ? 0 : ts;
};

/** "2026-09-26 08:34:40.710" → "26.09.2026 08:34" */
const formatInserted = (value) => {
  if (!value) return "-";
  const d = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return value;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const isFrontBody = (record) =>
  record.item === 2 ||
  String(record.material_type || "")
    .toLowerCase()
    .includes("ön");

export default function SapOrdersPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { setCurrentUrl } = useContext(TableContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [records, setRecords] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);

  // İstek parametreleri
  const [planIndex, setPlanIndex] = useState(1); // last_x_th_item
  const [orderBatchId, setOrderBatchId] = useState(null); // order_batch_id
  const [activeOnly, setActiveOnly] = useState(false);
  const [useBeta, setUseBeta] = useState(false);

  // İstemci tarafı filtreler
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL"); // ALL | GOVDE | GOVDE_ON
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL | ACTIVE | DONE

  const username = process.env.REACT_APP_USERNAME;
  const password = process.env.REACT_APP_SECRET_KEY;

  useEffect(() => {
    localStorage.setItem("urlpaintshop", location.pathname);
    if (setCurrentUrl) setCurrentUrl(location.pathname);
  }, [location.pathname, setCurrentUrl]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const params = {};
      if (orderBatchId) params.order_batch_id = orderBatchId;
      else params.last_x_th_item = planIndex;
      if (activeOnly) params.active_only = true;

      const endpoint = useBeta
        ? "/beta/external/sap/orders"
        : "/external/sap/orders";

      const res = await axios.get(`${apiUrl}${endpoint}`, {
        params,
        auth: { username, password },
      });

      const body = res.data || {};
      const list = Array.isArray(body.Records) ? body.Records : [];
      setRecords(list);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("SAP siparişleri çekilemedi:", err);
      if (err?.response?.status === 404) {
        setNotFound(true);
        setRecords([]);
      } else {
        setError(
          err?.response?.data?.message ||
            err?.response?.data?.detail ||
            err.message ||
            t("sapOrders.loadError")
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planIndex, orderBatchId, activeOnly, useBeta]);

  const uniqueMaterials = useMemo(
    () => [...new Set(records.map((r) => r.material).filter(Boolean))].sort(),
    [records]
  );
  const uniqueColors = useMemo(
    () => [...new Set(records.map((r) => r.color).filter(Boolean))].sort(),
    [records]
  );

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return records.filter((r) => {
      if (term) {
        const hay = [
          r.material,
          r.order_nr,
          r.material_description,
          r.color,
          r.version,
        ]
          .map((v) => String(v ?? "").toLowerCase())
          .join(" ");
        if (!hay.includes(term)) return false;
      }
      if (typeFilter === "GOVDE" && isFrontBody(r)) return false;
      if (typeFilter === "GOVDE_ON" && !isFrontBody(r)) return false;
      if (statusFilter === "ACTIVE" && !r.active) return false;
      if (statusFilter === "DONE" && r.active) return false;
      return true;
    });
  }, [records, searchTerm, typeFilter, statusFilter]);

  const metrics = useMemo(() => {
    const total = records.length;
    const active = records.filter((r) => r.active).length;
    const quantity = records.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
    const activeQuantity = records
      .filter((r) => r.active)
      .reduce((s, r) => s + (Number(r.quantity) || 0), 0);
    const first = records[0];
    return {
      total,
      active,
      done: total - active,
      quantity,
      activeQuantity,
      version: first?.version ?? first?.order_batch_id ?? null,
      insertedDate: first?.inserted_date ?? null,
      plant: first?.plant ?? null,
      orderType: first?.order_type ?? null,
    };
  }, [records]);

  const tableRef = useRef(null);
  const tableHeight = useTableBodyHeight(tableRef, 84, [loading, filteredData.length]);

  const handleExportExcel = () => {
    if (!filteredData.length) return;
    const rows = filteredData.map((r) => ({
      [t("sapOrders.export.sequence")]: r.sequence,
      [t("sapOrders.export.orderNr")]: r.order_nr,
      [t("sapOrders.export.startDate")]: r.start_date,
      [t("sapOrders.export.startTime")]: r.start_time,
      [t("sapOrders.export.endDate")]: r.end_date,
      [t("sapOrders.export.endTime")]: r.end_time,
      [t("sapOrders.export.material")]: r.material,
      [t("sapOrders.export.materialType")]: r.material_type,
      [t("sapOrders.export.description")]: r.material_description,
      [t("sapOrders.export.color")]: r.color,
      [t("sapOrders.export.orderType")]: r.order_type,
      [t("sapOrders.export.plant")]: r.plant,
      [t("sapOrders.export.quantity")]: r.quantity,
      [t("sapOrders.export.version")]: r.version,
      [t("sapOrders.export.active")]: r.active
        ? t("sapOrders.status.active")
        : t("sapOrders.status.done"),
      [t("sapOrders.export.id")]: r.id,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 6 },
      { wch: 12 },
      { wch: 12 },
      { wch: 10 },
      { wch: 12 },
      { wch: 10 },
      { wch: 14 },
      { wch: 10 },
      { wch: 32 },
      { wch: 12 },
      { wch: 10 },
      { wch: 8 },
      { wch: 8 },
      { wch: 10 },
      { wch: 12 },
      { wch: 10 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SAP");
    const dateStr = new Date().toISOString().split("T")[0];
    const version = metrics.version ? `_${metrics.version}` : "";
    XLSX.writeFile(wb, `SAP_Siparisleri${version}_${dateStr}.xlsx`);
  };

  const columns = [
    {
      title: t("sapOrders.columns.sequence"),
      dataIndex: "sequence",
      key: "sequence",
      width: 70,
      align: "center",
      sorter: (a, b) => a.sequence - b.sequence || a.item - b.item,
      defaultSortOrder: "ascend",
      render: (v, r) => (
        <span className="font-mono text-content-muted">
          {v}
          <span className="text-content-faint">.{r.item}</span>
        </span>
      ),
    },
    {
      title: t("sapOrders.columns.orderNr"),
      dataIndex: "order_nr",
      key: "order_nr",
      width: 100,
      render: (v) => <span className="font-mono text-content">{v || "-"}</span>,
    },
    {
      title: t("sapOrders.columns.start"),
      dataIndex: "start_date",
      key: "start",
      width: 150,
      sorter: (a, b) =>
        toTimestamp(a.start_date, a.start_time) -
        toTimestamp(b.start_date, b.start_time),
      render: (v, r) => (
        <span className="font-mono text-content">
          {formatDateTime(v, r.start_time)}
        </span>
      ),
    },
    {
      title: t("sapOrders.columns.end"),
      dataIndex: "end_date",
      key: "end",
      width: 150,
      sorter: (a, b) =>
        toTimestamp(a.end_date, a.end_time) - toTimestamp(b.end_date, b.end_time),
      render: (v, r) => (
        <span className="font-mono text-content">
          {formatDateTime(v, r.end_time)}
        </span>
      ),
    },
    {
      title: t("sapOrders.columns.material"),
      dataIndex: "material",
      key: "material",
      width: 140,
      filters: uniqueMaterials.map((m) => ({ text: m, value: m })),
      onFilter: (value, r) => r.material === value,
      filterSearch: true,
      render: (v) => (
        <span className="font-mono font-semibold text-content">{v}</span>
      ),
    },
    {
      title: t("sapOrders.columns.materialType"),
      dataIndex: "material_type",
      key: "material_type",
      width: 110,
      render: (v, r) =>
        isFrontBody(r) ? (
          <Tag color="purple" className="m-0">
            {v || t("sapOrders.type.frontBody")}
          </Tag>
        ) : (
          <Tag color="blue" className="m-0">
            {v || t("sapOrders.type.body")}
          </Tag>
        ),
    },
    {
      title: t("sapOrders.columns.description"),
      dataIndex: "material_description",
      key: "material_description",
      width: 260,
      ellipsis: { showTitle: false },
      render: (v) => (
        <Tooltip title={v}>
          <span className="text-content">{v || "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: t("sapOrders.columns.color"),
      dataIndex: "color",
      key: "color",
      width: 120,
      filters: uniqueColors.map((c) => ({ text: c, value: c })),
      onFilter: (value, r) => r.color === value,
      render: (v) => <span className="text-content">{v || "-"}</span>,
    },
    {
      title: t("sapOrders.columns.quantity"),
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      align: "right",
      sorter: (a, b) => a.quantity - b.quantity,
      render: (v) => (
        <span className="font-bold text-state-info">
          {Number(v || 0).toLocaleString("tr-TR")}
        </span>
      ),
    },
    {
      title: t("sapOrders.columns.version"),
      dataIndex: "version",
      key: "version",
      width: 100,
      align: "center",
      render: (v) => (
        <span className="font-mono text-content-muted">
          {v == null || v === "None" ? "-" : v}
        </span>
      ),
    },
    {
      title: t("sapOrders.columns.status"),
      dataIndex: "active",
      key: "active",
      width: 130,
      align: "center",
      filters: [
        { text: t("sapOrders.status.active"), value: true },
        { text: t("sapOrders.status.done"), value: false },
      ],
      onFilter: (value, r) => Boolean(r.active) === value,
      render: (v) =>
        v ? (
          <span className="inline-flex items-center gap-1 px-2 py-0 rounded-full text-2xs font-semibold leading-4 bg-sky-500/15 text-state-info border border-sky-500/30">
            <ClockCircleOutlined /> {t("sapOrders.status.active")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0 rounded-full text-2xs font-semibold leading-4 bg-emerald-500/15 text-state-ok border border-emerald-500/30">
            <CheckCircleOutlined /> {t("sapOrders.status.done")}
          </span>
        ),
    },
  ];

  const planOptions = PLAN_OPTIONS.map((n) => ({
    value: n,
    label:
      n === 1
        ? t("sapOrders.plan.latest")
        : t("sapOrders.plan.previous", { count: n - 1 }),
  }));

  return (
    <div className="w-full h-full p-3 laptop:p-4">
      {/* Üst Başlık & Eylemler */}
      <div className="mb-3 flex flex-col gap-2 laptop:flex-row laptop:items-center laptop:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-lg font-bold tracking-tight text-content laptop:text-xl">
              {t("sapOrders.title")}
            </h1>
            <span className="rounded-full bg-brand-500/15 px-3 py-1 text-xs font-semibold text-brand-600 dark:text-brand-300 border border-brand-500/30">
              {metrics.orderType || "PP20"} · {t("sapOrders.plant")}{" "}
              {metrics.plant || "5321"}
            </span>
            <Button
              type="text"
              size="small"
              shape="circle"
              icon={<InfoCircleOutlined className="text-base text-brand-500" />}
              onClick={() => setInfoOpen(true)}
            />
          </div>
          <p className="mt-0.5 max-w-3xl truncate text-xs text-content-muted">
            {t("sapOrders.subtitle")}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {lastUpdated && (
            <span className="hidden sm:flex items-center gap-1 text-xs text-content-muted mr-1">
              <ClockCircleOutlined /> {lastUpdated.toLocaleTimeString("tr-TR")}
            </span>
          )}
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={fetchData}
          >
            {t("sapOrders.refresh")}
          </Button>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            disabled={filteredData.length === 0}
          >
            {t("sapOrders.exportExcel")}
          </Button>
        </div>
      </div>

      {/* Hata */}
      {error && (
        <Alert
          type="error"
          showIcon
          message={t("sapOrders.loadErrorTitle")}
          description={error}
          action={
            <Button size="small" type="primary" danger onClick={fetchData}>
              {t("sapOrders.retry")}
            </Button>
          }
          className="mb-3"
        />
      )}

      {/* Metrik Kartları */}
      <div className="mb-3 grid grid-cols-2 laptop:grid-cols-4 gap-3">
        <div className="rounded-xl border border-line bg-surface px-3 py-2 shadow-card">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-content-muted">
              {t("sapOrders.metrics.total")}
            </span>
            <div className="flex h-6 w-6 items-center justify-center rounded-md text-xs bg-surface-sunken text-content-muted">
              <DatabaseOutlined />
            </div>
          </div>
          <div className="mt-0.5 flex items-baseline gap-2">
            <span className="text-xl font-extrabold text-content">
              {metrics.total}
            </span>
            <span className="text-xs text-content-muted">
              {t("sapOrders.metrics.records")}
            </span>
          </div>
        </div>

        <div className="rounded-xl border border-line bg-surface px-3 py-2 shadow-card">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-state-info">
              {t("sapOrders.metrics.active")}
            </span>
            <div className="flex h-6 w-6 items-center justify-center rounded-md text-xs bg-sky-500/20 text-state-info">
              <ClockCircleOutlined />
            </div>
          </div>
          <div className="mt-0.5 flex items-baseline gap-2">
            <span className="text-xl font-extrabold text-state-info">
              {metrics.active}
            </span>
            <span className="text-xs text-content-muted">
              {t("sapOrders.metrics.activeQuantity", {
                qty: metrics.activeQuantity.toLocaleString("tr-TR"),
              })}
            </span>
          </div>
        </div>

        <div className="rounded-xl border border-line bg-surface px-3 py-2 shadow-card">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-state-ok">
              {t("sapOrders.metrics.done")}
            </span>
            <div className="flex h-6 w-6 items-center justify-center rounded-md text-xs bg-emerald-500/20 text-state-ok">
              <CheckCircleOutlined />
            </div>
          </div>
          <div className="mt-0.5 flex items-baseline gap-2">
            <span className="text-xl font-extrabold text-state-ok">
              {metrics.done}
            </span>
            <span className="text-xs text-content-muted">
              {t("sapOrders.metrics.records")}
            </span>
          </div>
        </div>

        <div className="rounded-xl border border-line bg-surface px-3 py-2 shadow-card">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-content-muted">
              {t("sapOrders.metrics.version")}
            </span>
            <div className="flex h-6 w-6 items-center justify-center rounded-md text-xs bg-surface-sunken text-content-muted">
              <TagsOutlined />
            </div>
          </div>
          <div className="mt-0.5 flex items-baseline gap-2">
            <span className="text-xl font-extrabold text-content font-mono">
              {metrics.version ?? "-"}
            </span>
            <span className="text-xs text-content-muted">
              {metrics.insertedDate
                ? formatInserted(metrics.insertedDate)
                : t("sapOrders.metrics.noPlan")}
            </span>
          </div>
        </div>
      </div>

      {/* Araç Çubuğu */}
      <div className="mb-3 rounded-xl border border-line bg-surface px-3 py-2 shadow-card">
        <div className="flex flex-col gap-2 laptop:flex-row laptop:items-center laptop:justify-between">
          <div className="flex flex-wrap items-center gap-4">
            {/* Plan seçimi */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-content-muted flex items-center gap-1">
                <FilterOutlined /> {t("sapOrders.plan.label")}:
              </span>
              <Select
                value={planIndex}
                onChange={setPlanIndex}
                size="small"
                className="w-40"
                disabled={Boolean(orderBatchId)}
                options={planOptions}
              />
            </div>

            {/* Doğrudan order_batch_id */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-content-muted flex items-center gap-1">
                <NumberOutlined /> {t("sapOrders.plan.batchId")}:
              </span>
              <InputNumber
                size="small"
                min={1}
                className="w-28"
                placeholder={t("sapOrders.plan.batchIdPlaceholder")}
                value={orderBatchId}
                onChange={(v) => setOrderBatchId(v || null)}
              />
            </div>

            {/* Sadece aktif */}
            <div className="flex items-center gap-2">
              <Switch
                size="small"
                checked={activeOnly}
                onChange={setActiveOnly}
              />
              <Tooltip title={t("sapOrders.activeOnlyInfo")}>
                <span className="text-xs text-content-muted cursor-help">
                  {t("sapOrders.activeOnly")}
                </span>
              </Tooltip>
            </div>

            {/* Beta ortamı */}
            <div className="flex items-center gap-2">
              <Switch size="small" checked={useBeta} onChange={setUseBeta} />
              <Tooltip title={t("sapOrders.betaInfo")}>
                <span className="text-xs text-content-muted cursor-help">
                  {t("sapOrders.beta")}
                </span>
              </Tooltip>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="w-full laptop:w-64">
              <Input
                size="small"
                placeholder={t("sapOrders.searchPlaceholder")}
                prefix={<SearchOutlined className="text-content-faint" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
              />
            </div>

            <Radio.Group
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              size="small"
            >
              <Radio.Button value="ALL">{t("sapOrders.type.all")}</Radio.Button>
              <Radio.Button value="GOVDE">{t("sapOrders.type.body")}</Radio.Button>
              <Radio.Button value="GOVDE_ON">
                {t("sapOrders.type.frontBody")}
              </Radio.Button>
            </Radio.Group>

            <Radio.Group
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              size="small"
            >
              <Radio.Button value="ALL">{t("sapOrders.status.all")}</Radio.Button>
              <Radio.Button value="ACTIVE">
                {t("sapOrders.status.active")}
              </Radio.Button>
              <Radio.Button value="DONE">{t("sapOrders.status.done")}</Radio.Button>
            </Radio.Group>
          </div>
        </div>
      </div>

      {/* İçerik */}
      {loading ? (
        <div className="flex h-40 flex-col items-center justify-center gap-3 rounded-xl border border-line bg-surface">
          <Spin size="large" />
          <span className="text-sm text-content-muted">
            {t("sapOrders.loading")}
          </span>
        </div>
      ) : notFound ? (
        <div className="rounded-xl border border-line bg-surface shadow-card">
          <Result
            status="404"
            title={t("sapOrders.notFoundTitle")}
            subTitle={
              orderBatchId
                ? t("sapOrders.notFoundBatch", { id: orderBatchId })
                : t("sapOrders.notFoundPlan")
            }
            extra={
              <Button type="primary" onClick={fetchData}>
                {t("sapOrders.retry")}
              </Button>
            }
          />
        </div>
      ) : filteredData.length === 0 ? (
        <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-xl border border-line bg-surface text-center">
          <InfoCircleOutlined className="text-3xl text-content-faint" />
          <span className="text-base font-semibold text-content">
            {t("sapOrders.emptyTitle")}
          </span>
          <span className="text-xs text-content-muted">
            {t("sapOrders.emptyHint")}
          </span>
        </div>
      ) : (
        <div
          ref={tableRef}
          className="rounded-xl border border-line bg-surface overflow-hidden shadow-card"
        >
          <Table
            dataSource={filteredData}
            columns={columns}
            rowKey="id"
            size="small"
            className="sap-orders-table"
            bordered
            sticky
            scroll={{ x: 1450, y: tableHeight }}
            pagination={false}
            rowClassName={(r) =>
              r.active ? "" : "opacity-60 bg-surface-sunken"
            }
            footer={() => (
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-content-muted">
                <span>
                  {t("sapOrders.footer.shown", {
                    shown: filteredData.length,
                    total: records.length,
                  })}
                </span>
                <span>
                  {t("sapOrders.footer.quantity")}:{" "}
                  <span className="font-bold text-content">
                    {filteredData
                      .reduce((s, r) => s + (Number(r.quantity) || 0), 0)
                      .toLocaleString("tr-TR")}
                  </span>
                </span>
              </div>
            )}
          />
        </div>
      )}

      {/* Bilgi penceresi: ConfigProvider temasını alması için bileşen içinde */}
      <Modal
        open={infoOpen}
        onCancel={() => setInfoOpen(false)}
        className="info-modal"
        footer={
          <Button type="primary" onClick={() => setInfoOpen(false)}>
            {t("sapOrders.close")}
          </Button>
        }
        width={680}
        centered
        title={
          <div className="flex items-center gap-2">
            <InfoCircleOutlined className="text-brand-500" />
            <span className="font-bold text-content">{t("sapOrders.title")}</span>
          </div>
        }
      >
        <div className="mt-2 grid grid-cols-2 gap-2 rounded-lg border border-line bg-surface-sunken p-3 text-xs">
          <div>
            <div className="text-content-faint uppercase tracking-wider">
              {t("sapOrders.export.orderType")}
            </div>
            <div className="mt-0.5 font-mono font-semibold text-content">
              {metrics.orderType || "PP20"}
            </div>
          </div>
          <div>
            <div className="text-content-faint uppercase tracking-wider">
              {t("sapOrders.plant")}
            </div>
            <div className="mt-0.5 font-mono font-semibold text-content">
              {metrics.plant || "5321"}
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          {[
            { icon: <PartitionOutlined />, h: "h2", p: "p2" },
            { icon: <FieldTimeOutlined />, h: "h3", p: "p3" },
            { icon: <TagsOutlined />, h: "h4", p: "p4" },
          ].map((sec) => (
            <div key={sec.h} className="flex gap-3">
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-500/15 text-brand-600 dark:text-brand-300">
                {sec.icon}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-content">
                  {t(`sapOrders.info.${sec.h}`)}
                </div>
                <p className="mt-0.5 text-sm leading-relaxed text-content-muted">
                  {t(`sapOrders.info.${sec.p}`)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
